<template>
  <div
    ref="group"
    class="sp-finder-filter"
    :class="classModifiers"
    role="group"
    :aria-label="groupLabel"
    :aria-disabled="ariaDisabled"
  >
    <sp-text v-if="showsFieldLabel" tag="label" class="sp-finder-filter__label" aria-hidden="true">{{
      fieldLabel
    }}</sp-text>

    <component
      :is="filter"
      ref="filterInstance"
      v-bind="filterBindings"
      :model-value="model"
      :open="open"
      @update:model-value="handleFilterModelUpdate"
      @update:open="handleFilterOpenUpdate"
    >
      <template v-if="hasBarChrome" #activator="activatorProps">
        <FinderFilterChip
          v-bind="activatorProps"
          :prepend-icon="effectivePrependIcon"
          :active="active"
          :disabled="disabled"
        />
      </template>
      <template v-else-if="hasFieldChrome" #activator="activatorProps">
        <FinderFilterFieldActivator
          ref="fieldActivator"
          v-bind="activatorProps"
          :prepend-icon="effectivePrependIcon"
          :placeholder="fieldPlaceholder"
          :disabled="disabled"
          :aria-label="attrs.ariaLabel"
          :aria-labelledby="attrs.ariaLabelledby"
          :clearable="fieldClearable"
          @clear="deactivate"
        />
      </template>
      <template v-if="hasBooleanField" #field="fieldProps">
        <sp-checkbox
          v-preserve-properties:checked="fieldProps.checked"
          class="sp-finder-filter__checkbox"
          :label="fieldProps.label"
          :disabled="fieldProps.disabled"
          :data-testid="fieldProps.testId"
          @update-checked="fieldProps.handleUpdateChecked"
        ></sp-checkbox>
      </template>
    </component>

    <sp-button
      v-if="showClearAction"
      class="clear-action"
      icon="close-bold"
      color="white"
      size="xx-small"
      densitysmcompact="false"
      :label="clearActionLabel"
      :data-testid="TestId.finder.filterClear"
      @click="deactivate"
    ></sp-button>
  </div>
</template>

<script setup>
import { computed, nextTick, onMounted, provide, ref, useAttrs, useTemplateRef, watch } from "vue";
import { useExpose } from "#ce/composables/useExpose";
import { useAriaLabel } from "#ce/composables/useAriaLabel";
import { useBreakpoints } from "#ce/composables/useBreakpoints";
import { useForm } from "#ce/composables/useForm";
import { useFormControl } from "#ce/composables/useFormControl";
import { HOST_INJECTION_KEY, useHostElement } from "#ce/composables/useHostElement";
import { useHostEmit } from "#ce/composables/useHostEmit";
import { formAssociatedHost } from "#ce/framework/utils";
import {
  BAR_CHROME,
  FIELD_CHROME,
  FILTER_CHROME_ANCESTORS,
  FILTER_CHROME_ATTRIBUTE,
  FINDER_FILTER_BAR_TAG,
} from "#ce/components/SpFinder/engine/finder-filter-protocol";
import { tAccessibleName } from "#ce/shared/components-i18n";
import { t as tForm } from "#ce/shared/forms-i18n";
import { isPresent, toBoolean, trimToUndefined } from "#ce/utils/props";
import { TestId } from "#/test-ids";
import { useFinderFilterAnnouncement } from "#ce/components/SpFinderFilter/composables/useFinderFilterAnnouncement";
import { useFinderFilterScroll } from "#ce/components/SpFinderFilter/composables/useFinderFilterScroll";
import { useIgnoredAttributesWarning } from "#ce/components/SpFinderFilter/composables/useIgnoredAttributesWarning";
import FinderFilterChip from "#ce/components/SpFinderFilter/components/FinderFilterChip.ce.vue";
import FinderFilterFieldActivator from "#ce/components/SpFinderFilter/components/FinderFilterFieldActivator.ce.vue";
import { vPreserveProperties } from "#ce/utils/property-bindings";
import { filterComponents } from "./filter-components";
import { filterDeclares, forwardedPropsFor, props as finderFilterProps } from "./props";

// The filters submit form values via useForm() on this app's host.
// `inheritAttrs` is off, so the attributes that are no declared prop (`class`, `value`, `open`, …)
// stay on the host alone.
defineOptions({
  ...formAssociatedHost(),
  inheritAttrs: false,
});

const emit = defineEmits(["update-active", "update-open", "change", "close"]);
const { emitBubbling } = useHostEmit();

const props = defineProps(finderFilterProps);
const attrs = useAttrs();

// The host is form-associated, so native disabled state includes an ancestor
// `<fieldset disabled>`. The explicit leaf prop lets each filter preserve its
// native disabled semantics and accessibility tree.
const { disabled } = useFormControl({
  disabled: () => props.disabled,
});
const ariaDisabled = computed(() => disabled.value || null);

const model = ref(undefined);

const hasValue = computed(() => {
  const value = model.value;

  // The multi-param filters model a FormData and rebuild it empty when nothing is selected.
  if (value instanceof FormData) {
    return value.keys().next().done === false;
  }

  if (Array.isArray(value)) {
    return value.length > 0;
  }

  return value !== undefined && value !== "";
});

// The filters announce their initial model as an immediate update:model-value at setup; the
// host's change reports only what happens after that.
const isSettled = ref(false);

function markSettled() {
  isSettled.value = true;
}

function settleInitialModel() {
  return nextTick(markSettled);
}

onMounted(settleInitialModel);

// A scripted write's round-trip through the filter can carry a fresh object (ProviderFilter
// rebuilds a FormData per recomputation), which the identity guard below cannot recognize — the
// scripted entry points hold this window open through their watcher flush instead.
let isScriptedWrite = false;

function finishScriptedWrite() {
  isScriptedWrite = false;
}

function writeSilently(write) {
  isScriptedWrite = true;
  write();
  nextTick(finishScriptedWrite);
}

// The inner filter reports the user's pick through update:model-value. The exposed `value`
// setter and the mount-time attribute seed write the model silently (their round-trips
// arrive back here).
//
// `options.seeded` marks an update that carries the value the mount was given rather than a pick:
// it moves the model and dispatches nothing. A filter that resolves its seed through a request
// sets it, since that update lands after the settle tick.
function handleFilterModelUpdate(value, options) {
  if (value === model.value) {
    return;
  }

  model.value = value;

  if (isSettled.value && !isScriptedWrite && !options?.seeded) {
    const metadata = {};
    if (options?.locationDisplayName) {
      metadata.locationDisplayName = options.locationDisplayName;
    }
    if (options?.transientLocation) {
      metadata.transientLocation = true;
    }
    if (options?.locationSelected) {
      metadata.locationSelected = true;
    }
    if (Object.keys(metadata).length) {
      emitBubbling("change", value, metadata);
    } else {
      emitBubbling("change", value);
    }
  }
}

const host = useHostElement();

// The filters are nested children, so `instance.ce` is unset for them and `useHostElement` falls
// back to injection. Without this they resolve a null host, and `useForm` then mirrors no form
// value and logs nothing.
provide(HOST_INJECTION_KEY, host);

/** The enclosing bar, or null when the filter stands alone. Resolved once, at setup. */
const bar = host?.closest(FINDER_FILTER_BAR_TAG) ?? null;

// Leaf filters style from the host's `chrome` attribute, including chrome inferred from an ancestor.
const hasBarChrome =
  props.chrome !== undefined ? props.chrome === BAR_CHROME : host?.closest(FILTER_CHROME_ANCESTORS) != null;

if (hasBarChrome) {
  host?.setAttribute(FILTER_CHROME_ATTRIBUTE, BAR_CHROME);
}

const hasFieldChrome = computed(() => props.chrome === FIELD_CHROME);
const fieldActivator = useTemplateRef("fieldActivator");
const fieldLabel = computed(() => fieldActivator.value?.label ?? props.label);

const type = computed(() => trimToUndefined(props.type));

const hasBooleanField = computed(() => hasFieldChrome.value && type.value === "boolean");

const filter = computed(() => filterComponents[type.value]);

useIgnoredAttributesWarning(type);

const forwardedNames = computed(() => Object.keys(forwardedPropsFor(type.value)));

const filterInstance = useTemplateRef("filterInstance");
const filterInstanceProps = computed(() => filterInstance.value?.props);
const suppressesPrependIcon = computed(() => toBoolean(props.noPrependIcon));
const suppressesFieldLabel = computed(() => toBoolean(props.noFieldLabel));
const showsFieldLabel = computed(() => hasFieldChrome.value && !suppressesFieldLabel.value);

// All presentations, the public legacy property, and filter-bar announcements read this one
// resolved value. The leaf still receives an empty value while suppressed to override its default.
const effectivePrependIcon = computed(() =>
  suppressesPrependIcon.value ? undefined : filterInstanceProps.value?.prependIcon,
);

function isSet(name) {
  return props[name] !== undefined;
}

const filterBindings = computed(() => {
  const bindings = Object.fromEntries(forwardedNames.value.filter(isSet).map((name) => [name, props[name]]));

  if (filterDeclares(type.value, "disabled")) {
    bindings.disabled = disabled.value;
  }

  if (filterDeclares(type.value, "name") && formName.value) {
    bindings.name = formName.value;
  }

  // An empty string is the leaf-level override for a filter's own default icon. The public API
  // exposes undefined instead, through effectivePrependIcon, so consumers never render it.
  if (filterDeclares(type.value, "prependIcon")) {
    const prependIcon = suppressesPrependIcon.value ? "" : attrs.prependIcon;
    if (prependIcon !== undefined) {
      bindings.prependIcon = prependIcon;
    }
  }

  // A chip's menu hangs under the chip, so it takes no pointer.
  if (hasBarChrome && filterDeclares(type.value, "hidePointer")) {
    bindings.hidePointer = true;
  }

  if (hasBarChrome && filterDeclares(type.value, "location")) {
    bindings.location = "bottom-left";
  }

  return bindings;
});

const isNonClearable = computed(() => toBoolean(props.nonClearable));

// Category and insurance enforce a value when non-clearable, as well as when explicitly required.
// Inner custom elements own separate ElementInternals, so mirror that required state where the
// finder filter owns the submitted value.
const nonClearableRequiresValue = computed(
  () => isNonClearable.value && ["category", "insurance"].includes(type.value),
);
const isRequired = computed(
  () => filterDeclares(type.value, "required") && (isPresent(props.required) || nonClearableRequiresValue.value),
);
const requiredMessage = computed(() => tForm(type.value === "date" ? "date_value_missing" : "select_value_missing"));

// `inheritAttrs: false` leaves consumer ARIA naming on the host. An `aria-labelledby` IDREF does
// not cross its shadow boundary, so the group reflects its resolved elements through useAriaLabel.
const group = useTemplateRef("group");
const { isGtSmScreen } = useBreakpoints();
const hasNamedGroup = computed(() => !hasFieldChrome.value || (hasBooleanField.value && isGtSmScreen.value));
const { ariaLabel: groupLabel } = useAriaLabel(group, {
  label: computed(() => (hasNamedGroup.value ? (attrs.ariaLabel ?? props.label) : undefined)),
  labelledby: computed(() => (hasNamedGroup.value ? attrs.ariaLabelledby : undefined)),
});

// A filter that submits background values states its own opinion. The rest are active when they
// hold a value.
const active = computed(() => {
  const declared = filterInstance.value?.active;

  return declared === undefined ? hasValue.value : Boolean(declared);
});

function reportActive(value) {
  emit("update-active", value);
}

// Reporting starts at mount, once the template ref has resolved, so a filter's own opinion shapes
// the first value. A filter active from birth never transitions, so it is announced here.
function startReportingActive() {
  if (active.value) {
    reportActive(true);
  }

  watch(active, reportActive);
}

onMounted(startReportingActive);

// `open` is live state this element owns, so it is not a declared prop: Vue installs a
// non-configurable host accessor for every prop, whose getter reports the attribute. The attribute
// names the state to mount at, and the legacy filter menus read and write the property.
const open = ref(!disabled.value && isPresent(attrs.open));

function closeWhenDisabled(value) {
  if (value) {
    open.value = false;
  }
}

watch(disabled, closeWhenDisabled, {
  flush: "sync",
});

function handleFilterOpenUpdate(value) {
  open.value = !disabled.value && toBoolean(value);
}

function adoptOpenAttribute(value) {
  open.value = !disabled.value && isPresent(value);
}

watch(() => attrs.open, adoptOpenAttribute);

function reportOpen(value) {
  emit("update-open", value);

  if (!value) {
    emit("close");
  }
}

watch(open, reportOpen);

useFinderFilterScroll(host, open, hasBarChrome);

const classModifiers = computed(() => ({
  "--is-active": active.value,
  "--field": hasFieldChrome.value,
  "--boolean": type.value === "boolean",
}));

const showClearAction = computed(() => hasBarChrome && active.value && !disabled.value && !isNonClearable.value);
const fieldClearable = computed(() => hasValue.value && !disabled.value && !isNonClearable.value);

const clearActionLabel = computed(() => tAccessibleName("finder_filter_clear_action"));

const { exposeProperties, exposeMethods } = useExpose();

/** Host API consumed by the legacy events menu and doctor finder wizard. */
exposeProperties({
  isFilterApp: {
    value: true,
  },
  open: {
    set: (value) => (open.value = !disabled.value && toBoolean(value)),
    get: () => open.value,
  },
  prependIcon: {
    get: () => effectivePrependIcon.value,
  },
  value: {
    set: (value) => writeSilently(() => (model.value = trimToUndefined(value))),
    get: () => model.value,
  },
  active: {
    get: () => active.value,
  },
  /**
   * Multi-value filters (the Provider panel) own several search params. The
   * doctor-finder bridge reads this list to learn which params the one element
   * carries. Single-value filters leave it undefined and fall back to `name`.
   */
  filterNames: {
    get: () => filterInstance.value?.filterNames,
  },
  /**
   * The current selection's display wording, read by the doctor-finder bridge for the
   * search analytics. Undefined for filters that do not resolve their selection to
   * text, and while nothing is selected.
   *
   * TODO: Remove with the rest of this bridge — https://github.com/combinaut/sparkle/issues/29500
   */
  selectedText: {
    get: () => filterInstance.value?.selectedText,
  },
  /**
   * A multi-value filter's selection wording per param, keyed by the names in `filterNames`, for
   * the doctor-finder bridge reading one param's wording out of a filter whose `selectedText`
   * summarises several. Undefined for single-value filters.
   *
   * TODO: Remove with the rest of this bridge — https://github.com/combinaut/sparkle/issues/29500
   */
  selectedTexts: {
    get: () => filterInstance.value?.selectedTexts,
  },
});

function reportClearedModel() {
  if (model.value === undefined) {
    emitBubbling("change", undefined);
  }
}

/**
 * Clears the filter, dispatching one host `change` with the cleared state. The dispatch waits for
 * the flush, so the filter has emptied its form value by the time a listener reads the form. A
 * filter that rebuilds an empty model from the clear (the Provider panel) supplies that dispatch
 * through `handleFilterModelUpdate`. When no rebuilt model has arrived by the flush, the host
 * dispatches `undefined` itself. A no-op when nothing is applied or the filter is non-clearable.
 */
function deactivate() {
  if (disabled.value || isNonClearable.value) {
    return;
  }

  // Clearing still runs with nothing applied, to retire a seed whose request is still out. It
  // dispatches nothing then: a filter rebuilds its model on the write, and a rebuilt empty model
  // reports a change the visitor never made.
  if (!hasValue.value) {
    writeSilently(() => (model.value = undefined));

    return;
  }

  model.value = undefined;
  nextTick(reportClearedModel);
}

exposeMethods({
  deactivate,
  /**
   * Restore a multi-value filter's sub-selections from URL params (deep-link,
   * back/forward). No-op for single-value filters that don't implement it.
   */
  setFilterValues(values, metadata) {
    writeSilently(() => {
      if (metadata === undefined) {
        filterInstance.value?.setFilterValues?.(values);
      } else {
        filterInstance.value?.setFilterValues?.(values, metadata);
      }
    });
  },
});

function restoreValueAttribute() {
  writeSilently(adoptValueAttribute);
}

function adoptValueAttribute() {
  model.value = trimToUndefined(attrs.value);
}

function seedInitialValue() {
  if (attrs.value) {
    restoreValueAttribute();
  }
}

onMounted(seedInitialValue);

const { name: formName, mirrorValueMissing, onFormReset } = useForm();

// The child's active state reflects its normalized selection; a raw seed such as "[]" can
// remain in the host model even though the child selects and submits nothing.
mirrorValueMissing(() => isRequired.value && !active.value, requiredMessage);

// A form reset restores the `value` attribute silently, the way a native control returns to its
// content attribute: the filter adopts the model and no change is dispatched.
onFormReset(restoreValueAttribute);

const withheld = computed(() => props.prerequisite === "unmet" && !active.value && !hasValue.value && !open.value);

function syncWithheldVisibility(value) {
  host?.toggleAttribute("hidden", value);
}

watch(withheld, syncWithheldVisibility, {
  immediate: true,
});

useFinderFilterAnnouncement(bar, {
  open,
  readState: () =>
    Object.freeze({
      active: active.value,
      open: open.value,
      withheld: withheld.value,
      clearable: !disabled.value && !isNonClearable.value,
    }),
  describe: () => ({
    name: formName.value,
    label: props.label ?? "",
    icon: effectivePrependIcon.value,
  }),
  clear: deactivate,
});
</script>

<style>
:host {
  display: block;
  width: 100%;
}

.sp-finder-filter {
  position: relative;
}

.clear-action {
  position: absolute;
  top: var(--sp-ref-scale-4);
  right: calc(var(--sp-ref-scale-12) / 2);
  opacity: 0.3;
  z-index: 2;
  --sp-comp-button-white-text-color: var(--sp-sys-color-primary) !important;
}

/* The filter-bar menus behave like the legacy filter menus beside them: a 0.5rem gap below the
   chip (SpPopup's 4px default reads as "stuck" next to the native <dialog> ones), and they open
   downward only, because a chip sits directly under the search field and a menu placed above it
   would cover the field. Both tokens inherit into every filter-bar type, and page CSS targeting
   the element overrides them. */
:host([chrome="bar"]) {
  --sp-comp-popup-offset: 0.5rem;
  --sp-comp-popup-flip: false;
}

:host([hidden]) {
  display: none;
}

:host(:focus),
:host(:focus-visible) {
  outline: none;
  box-shadow: none !important;
}
</style>

<style scoped lang="scss">
.sp-finder-filter.--field {
  font: var(--sp-comp-finder-filter-field-font, inherit);

  .sp-finder-filter__label {
    --sp-comp-text-label-short: var(--sp-comp-finder-filter-field-label-font, var(--sp-sys-form-field-label-short));
    --sp-comp-text-color: var(--sp-comp-finder-filter-field-label-color, var(--sp-sys-color-on-surface));

    display: block;
    text-transform: var(--sp-comp-finder-filter-field-label-transform, none);
    padding-block-end: var(--sp-comp-finder-filter-field-label-gap, var(--sp-ref-scale-6));

    &::part(base) {
      vertical-align: var(--sp-comp-finder-filter-field-label-align, baseline);
    }
  }

  .sp-finder-filter__checkbox {
    --sp-comp-checkbox-control-display: var(--sp-comp-finder-filter-field-checkbox-control-display, grid);
    --sp-comp-checkbox-input-display: var(--sp-comp-finder-filter-field-checkbox-input-display, flex);
    --sp-comp-checkbox-input-contain: var(--sp-comp-finder-filter-field-checkbox-input-contain, none);
    --sp-comp-checkbox-label-display: var(--sp-comp-finder-filter-field-checkbox-label-display, inline);
    --sp-comp-checkbox-label-align: var(--sp-comp-finder-filter-field-checkbox-label-align, baseline);
    --sp-comp-checkbox-label-margin-inline: var(--sp-comp-finder-filter-field-checkbox-label-margin-inline, 0);
    --sp-comp-checkbox-size: var(--sp-comp-finder-filter-field-checkbox-size, var(--sp-ref-scale-18));
    --sp-comp-checkbox-gap: var(--sp-comp-finder-filter-field-checkbox-gap, var(--sp-ref-scale-8));
    --sp-comp-checkbox-offset-block: var(--sp-comp-finder-filter-field-checkbox-offset-block);
    --sp-comp-checkbox-border-radius: var(
      --sp-comp-finder-filter-field-checkbox-border-radius,
      var(--sp-sys-shape-control)
    );
    --sp-comp-checkbox-border-width: var(--sp-comp-finder-filter-field-checkbox-border-width, 1px);
    --sp-comp-checkbox-border-color: var(
      --sp-comp-finder-filter-field-checkbox-border-color,
      var(--sp-sys-color-outline)
    );
    --sp-comp-checkbox-background-color: var(
      --sp-comp-finder-filter-field-checkbox-background,
      var(--sp-sys-color-surface)
    );
    --sp-comp-checkbox-label-font: var(--sp-comp-finder-filter-field-option-font, var(--sp-sys-form-field-label-short));
    --sp-comp-text-label-short: var(--sp-comp-finder-filter-field-option-font, var(--sp-sys-form-field-label-short));
    --sp-comp-text-color: var(--sp-comp-finder-filter-field-label-color, var(--sp-sys-color-on-surface));

    box-sizing: border-box;
    height: var(--sp-comp-finder-filter-field-boolean-height, var(--sp-ref-scale-40));
    padding-block: var(--sp-comp-finder-filter-field-checkbox-padding-block, var(--sp-ref-scale-4));
  }
}
</style>
